import { AlertCircle, Clock } from "lucide-react";
import React from "react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useQuoteCountdown } from "@/shared/hooks/useQuoteCountdown";

/**
 * Copy is passed in rather than read from a namespace, for the same reason `QuoteBreakdown` does it: this
 * banner renders inside both the dispersion wizard and the swap form, and a shared component cannot guess
 * which namespace owns the strings.
 */
export interface QuoteCountdownLabels {
  /** Prefix before the clock, e.g. "Rate valid for". The time is appended by the component. */
  validFor: string;
  /** Extra line shown in the last minute, e.g. "Expiring soon — confirm or re-quote". */
  expiringSoon: string;
  /** Replaces the whole banner once the quote is dead. */
  expired: string;
}

export interface QuoteCountdownTimerProps {
  expiresAt: string | null | undefined;
  labels: QuoteCountdownLabels;
  /** Fired once when the countdown reaches zero — the host blocks submission and offers a re-quote. */
  onExpire?: () => void;
  className?: string;
}

/**
 * The "this price dies at HH:MM" banner over a frozen quote.
 *
 * Three states, escalating: informational while there is time, `warning` in the final minute, `destructive`
 * once expired. The countdown is a courtesy — the real guard is the backend rejecting a redeemed or expired
 * quote — so the host must still handle `quote_expired` on submit.
 *
 * @summary countdown banner for a frozen quote, with a warning and an expired state
 */
export const QuoteCountdownTimer: React.FC<QuoteCountdownTimerProps> = ({
  expiresAt,
  labels,
  onExpire,
  className,
}) => {
  const { isExpired, minutes, formattedTime } = useQuoteCountdown(expiresAt, onExpire);

  if (!expiresAt) return null;

  if (isExpired) {
    return (
      <ColocaAlert
        inline
        variant="destructive"
        icon={AlertCircle}
        text={labels.expired}
        className={className}
      />
    );
  }

  // Under a minute left: escalate so the user does not start typing recipient details against a dead price.
  const isWarning = minutes === 0;

  return (
    <ColocaAlert
      inline
      variant={isWarning ? "warning" : "secondary"}
      icon={Clock}
      className={className}
      text={
        <>
          {labels.validFor} <span className="font-semibold tabular-nums">{formattedTime}</span>
          {isWarning && <span className="mt-1 block text-xs">{labels.expiringSoon}</span>}
        </>
      }
    />
  );
};
